import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createInitialGameState } from './pokerConfig';
import { deriveCurrentAction } from './pokerGameIterator';
import { savePokerHandForUser } from './hands-api';
import { useAuth } from 'components/auth/context/useAuth';

const PokerGameContext = createContext(null);

export const usePokerGame = () => {
  const ctx = useContext(PokerGameContext);
  if (!ctx) {
    throw new Error('usePokerGame must be used within a PokerGameProvider');
  }
  return ctx;
};

export const PokerGameProvider = ({ children }) => {
  const { user } = useAuth();
  const [gameState, setGameState] = useState(() => createInitialGameState());
  const [currentAction, setCurrentAction] = useState({
    street: 'preflop',
    playerToAct: null,
    potSize: 0,
    amountToCall: 0,
    raiseIndex: 0,
    validActions: [],
  });

  useEffect(() => {
    const newCurrentAction = deriveCurrentAction(gameState);
    setCurrentAction(newCurrentAction);
  }, [gameState]);

  const addAction = useCallback((action) => {
    setGameState((prev) => {
      const street = deriveCurrentAction(prev).street;
      const updatedActions = {
        ...prev.actions,
        [street]: [...(prev.actions[street] || []), { ...action, timestamp: Date.now() }],
      };

      return {
        ...prev,
        actions: updatedActions,
      };
    });
  }, []);

  const saveHand = useCallback(async () => {
    if (!user) {
      throw new Error('You must be logged in to save a hand.');
    }
    return savePokerHandForUser(user.uid, { ...gameState });
  }, [gameState, user]);

  const value = {
    gameState,
    currentAction,
    addAction,
    saveHand,
  };

  return (
    <PokerGameContext.Provider value={value}>
      {children}
    </PokerGameContext.Provider>
  );
};
